import { useSelector } from 'react-redux';
import { startCase } from 'lodash';
import {
  OnboardingChrome,
  DurationSlideOutContent,
  CareHoursStartSlideOutContent,
  CareHoursEndSlideOutContent,
  Badge,
} from '../components';
import { selectClient, selectIntakeSurvey } from '../model';
import { possessivePronoun } from '../util';
import { SlideContent, SlideOutSheet } from '../components';

import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { carePhaseDefs, formatDurationYears, CarePhase, isDurationCustom } from '@shared';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import {
  useAnimationPreferences,
  AnimationToggle,
  CareIntensityAnimation,
} from '@client/features/client-onboarding';

export function OnboardingPredictedCareNeeds(_props: ClientIdProps) {
  const {
    clientFirstName,
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = useSelector(selectClient);
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { animationsEnabled } = useAnimationPreferences();
  const { flags } = useFeatureFlags();
  const showAnimations = flags.animatedSlides && animationsEnabled;

  const weeksPerMonth = 4.33;
  const lengthOfCareYears = allPhaseCareMonthsNeeded / 12;

  const earlyPhaseIndex = carePhaseDefs[CarePhase.earlyCare].index;
  const earlyPhaseCosts = phaseCosts[earlyPhaseIndex];
  const initialHoursPerWeek =
    earlyPhaseCosts.phaseCareHoursNeeded / earlyPhaseCosts.phaseCareMonthsNeeded / weeksPerMonth;

  const latePhaseIndex = carePhaseDefs[CarePhase.fullCare].index;
  const latePhaseCosts = phaseCosts[latePhaseIndex];
  const laterHoursPerWeek =
    latePhaseCosts.phaseCareHoursNeeded / latePhaseCosts.phaseCareMonthsNeeded / weeksPerMonth;

  return (
    <OnboardingChrome
      slidePage={OnboardingPredictedCareNeeds}
      nextButtonLabel={`See ${clientFirstName}'s Costs`}
    >
      <SlideContent
        headerText={`${clientFirstName}'s predicted care needs`}
        headerAction={flags.animatedSlides ? <AnimationToggle /> : undefined}
      >
        {showAnimations && (
          <CareIntensityAnimation
            clientFirstName={clientFirstName}
            lengthOfCareYears={lengthOfCareYears}
            initialHoursPerWeek={initialHoursPerWeek}
            laterHoursPerWeek={laterHoursPerWeek}
            intakeSurvey={intakeSurvey}
            isDurationEdited={isDurationCustom(phaseCosts)}
          />
        )}
        <div className={showAnimations ? 'hidden' : undefined}>
          <div className="flex flex-col gap-4 pb-0 pt-4 lg:flex-row lg:gap-5 lg:pb-12 lg:pt-12">
            <ColumnLengthOfCareYears />
            <ColumnCareHoursLtcStart />
            <ColumnCareHoursLtcEnd />
          </div>
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

function ColumnLengthOfCareYears() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const {
    phaseCosts,
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = useSelector(selectClient);
  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
  return (
    <div
      className="flex-grow-1 border-primary-200 flex basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predicted-care-needs-length"
    >
      <div
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predicted-duration-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} length of care:
        <SlideOutSheet
          slideOutContent={<DurationSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-500 hover:text-gray-700"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </div>
      <div
        className="text-purple py-4 text-5xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predicted-duration-value"
      >
        {formatDurationYears(ltcDurationYears)}
      </div>
      <div
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predicted-duration-unit"
      >
        years
        {isDurationCustom(phaseCosts) && (
          <Badge
            color="gray"
            label="edited"
            className="ml-1"
          />
        )}
      </div>
    </div>
  );
}

function ColumnCareHoursLtcStart() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { phaseCosts } = useSelector(selectClient);
  const phaseIndex = carePhaseDefs[CarePhase.earlyCare].index;
  const earlyCarePhaseCosts = phaseCosts[phaseIndex];
  const { phaseCareHoursNeeded, phaseCareMonthsNeeded } = earlyCarePhaseCosts;
  const weeksPerMonth = 4.33;
  const hoursPerWeek = phaseCareHoursNeeded / phaseCareMonthsNeeded / weeksPerMonth;

  return (
    <div
      className="flex-grow-1 border-primary-200 flex basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predicted-care-needs-initial-hours"
    >
      <div
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predicted-initial-hours-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} initial care needs:
        <SlideOutSheet
          slideOutContent={<CareHoursStartSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-500 hover:text-gray-700"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </div>
      <div
        className="text-purple py-4 text-5xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predicted-initial-hours-value"
      >
        {Math.round(hoursPerWeek)}
      </div>
      <div
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predicted-initial-hours-unit"
      >
        hours/week
      </div>
    </div>
  );
}

function ColumnCareHoursLtcEnd() {
  const { phaseCosts } = useSelector(selectClient);
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const phaseIndex = carePhaseDefs[CarePhase.fullCare].index;
  const earlyCarePhaseCosts = phaseCosts[phaseIndex];
  const { phaseCareHoursNeeded, phaseCareMonthsNeeded } = earlyCarePhaseCosts;
  const weeksPerMonth = 4.33;
  const hoursPerWeek = phaseCareHoursNeeded / phaseCareMonthsNeeded / weeksPerMonth;

  return (
    <div
      className="flex-grow-1 border-primary-200 flex basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predicted-care-needs-later-hours"
    >
      <div
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predicted-later-hours-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} later care needs:
        <SlideOutSheet
          slideOutContent={<CareHoursEndSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-500 hover:text-gray-700"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </div>
      <div
        className="text-purple py-4 text-5xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predicted-later-hours-value"
      >
        {Math.round(hoursPerWeek)}
      </div>
      <div
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predicted-later-hours-unit"
      >
        hours/week
      </div>
    </div>
  );
}
